/**
 * @fileoverview A page's event: its durable name, and either its body or the
 * means to find it.
 *
 * The event is addressable, so `kind:pubkey:d` is a STABLE address that
 * survives every edit. That is the whole reason the URL holds an `naddr` and
 * not the blob hash or the event id -- both of those change when the page is
 * republished, breaking every link ever shared.
 *
 * TWO KINDS REACH THIS PARSER and they differ in where the body is:
 *
 *   30023  NIP-23 long-form. SOMEBODY ELSE'S ARTICLE, or an author's own
 *          written in another client. The markdown is in `.content` and there
 *          is no blob at all. We have not published one since 2026-09-16 and do
 *          not; reading them is a capability of the reader that costs nothing.
 *          Six of the seven clients driven on 2026-09-15 render this kind from
 *          a relay hint alone. The seventh, Alexandria, ignores the hint and so
 *          never finds the event; see `kinds.ts`.
 *   33301  Ours, and EVERY page we publish: markdown, raw HTML, public and
 *          private alike. The body is always a blob, and the event carries its
 *          hash and a list of servers to try.
 *
 * Both produce a `PageIndex`; `source` is what they disagree about, and it is a
 * union rather than an optional hash so that a caller cannot forget to check
 * which it has.
 *
 * SHAPE OF A 30023, per NIP-23 -- these are the spec's own tags, not ours:
 *
 *   d            the page's stable identifier             REQUIRED
 *   title        human title
 *   summary      one-line description
 *   published_at unix seconds
 *   .content     THE MARKDOWN
 *
 * SHAPE OF A 33301, ours, because nothing specified it:
 *
 *   d          the page's stable identifier             REQUIRED
 *   x          sha256 of the content blob, lowercase    REQUIRED
 *   format     "markdown" | "html"                      REQUIRED
 *   title      human title
 *   summary    one-line description
 *   m          blob mime type
 *   server     a Blossom server hint; REPEATABLE
 *   published_at / updated_at   unix seconds
 *
 * AND ON BOTH, ignorable by anyone who does not know them:
 *
 *   theme      "<kind>:<pubkey>:<d>" of a theme event
 *   toc / width / columns       per-page layout hints
 *
 * Those last four ride on OUR kind now. They were designed to ride on a 30023
 * as tags other clients drop on the floor, which is the graceful-degradation
 * contract, and the parser still reads them off either kind so that a page
 * published before 2026-09-16 keeps its theme.
 *
 * `.content` ON A 33301 holds a plaintext abstract for a public page -- kept
 * plaintext deliberately, because NIP-50 search on the relay is what makes
 * public pages findable for free.
 *
 * ON A PRIVATE PAGE `.content` IS EMPTY AND THE CIPHERTEXT IS IN A TAG. That is
 * the opposite of what a collection does one level up and it is deliberate. An
 * earlier draft of this comment said the ciphertext would live here. Measured
 * 2026-09-15: a 33301 carrying ciphertext inline was opened in six live clients
 * and four printed it as the article, six hundred characters of base64 under a
 * title. Most clients print whatever `.content` they are handed, whosever kind
 * it arrived under, so the protective property is that there is nothing in it.
 * If anyone later makes this consistent with the collection, that is the
 * regression. The parser must not treat content as structure on this kind
 * either way.
 *
 * WHY `server` IS REPEATABLE AND THERE IS NO `url` TAG. A single URL binds the
 * page to one host, and the portability claim is that leaving works. The
 * renderer is given a hash plus a list of places to try, and it verifies the
 * hash itself -- so a stranger's mirror is safe to read from without trusting
 * it, and moving hosts breaks nothing.
 */

import { NIP23_LONG_FORM_KIND, PAGE_INDEX_KIND } from './kinds';

/** The two formats. Two is a decision; three is drift. */
export type PageFormat = 'markdown' | 'html';

/**
 * Where a page's body is.
 *
 * A union and not an optional `blobHash`, so that the renderer has to say which
 * case it is handling. An optional field invites `index.blobHash!`, and the day
 * that is wrong is the day a public markdown page tries to fetch a blob that
 * was never uploaded.
 */
export type PageSource =
  /** 30023: the markdown was in the event. Nothing to fetch. */
  | { kind: 'inline'; markdown: string }
  /** 33301: fetch these bytes and check them against this hash. */
  | { kind: 'blob'; hash: string; mimeType: string; serverHints: string[] };

/** Per-page layout hints. Properties of THIS page, not of a shared theme. */
export interface PageLayout {
  toc: boolean;
  width: 'narrow' | 'prose' | 'wide';
  columns: 1 | 2;
}

export interface PageIndex {
  /** The addressable coordinate. This is the page's durable name. */
  address: { kind: number; pubkey: string; identifier: string };
  title: string;
  summary: string;
  format: PageFormat;
  source: PageSource;
  /** Addressable coordinate of a theme event, verbatim, or null. */
  themeAddress: string | null;
  layout: PageLayout;
  publishedAt: number | null;
  updatedAt: number | null;
  /** The event's own created_at, used to pick the newest of several copies. */
  createdAt: number;
}

/** The minimum of a Nostr event this module needs. Keeps NDK/nostr-tools out. */
export interface RawEvent {
  kind: number;
  pubkey: string;
  created_at: number;
  content: string;
  tags: string[][];
}

const HEX_64 = /^[0-9a-f]{64}$/;
const DEFAULT_LAYOUT: PageLayout = { toc: false, width: 'prose', columns: 1 };

function firstTag(tags: string[][], name: string): string | undefined {
  return tags.find((t) => t[0] === name && t.length > 1)?.[1];
}

function allTags(tags: string[][], name: string): string[] {
  return tags
    .filter((t) => t[0] === name && typeof t[1] === 'string')
    .map((t) => t[1] as string);
}

/**
 * Parse a page event of either kind, or return null if it is not one we can
 * render.
 *
 * NULL RATHER THAN THROW, and the reason is a product requirement rather than a
 * style preference. Every way a page can fail to load -- no event, a malformed
 * event, an unreachable blob, a hash that does not match, ciphertext we hold no
 * key for -- has to reach the reader as the SAME outcome, because a private
 * page must be indistinguishable from a missing one to a stranger holding its
 * address. Distinct exception types are exactly the thing that leaks the
 * difference, so this layer does not create any.
 */
export function parseIndexEvent(event: RawEvent): PageIndex | null {
  if (!HEX_64.test(event.pubkey)) return null;

  const identifier = firstTag(event.tags, 'd');
  if (identifier === undefined) return null;

  const parsed =
    event.kind === NIP23_LONG_FORM_KIND
      ? parseLongForm(event)
      : event.kind === PAGE_INDEX_KIND
        ? parseBlobBacked(event)
        : null;
  if (!parsed) return null;

  return {
    address: { kind: event.kind, pubkey: event.pubkey, identifier },
    title: firstTag(event.tags, 'title') ?? identifier,
    summary: firstTag(event.tags, 'summary') ?? '',
    format: parsed.format,
    source: parsed.source,
    themeAddress: firstTag(event.tags, 'theme') ?? null,
    layout: parseLayout(event.tags),
    publishedAt: parseUnix(firstTag(event.tags, 'published_at')),
    updatedAt: parseUnix(firstTag(event.tags, 'updated_at')),
    createdAt: event.created_at,
  };
}

/**
 * A NIP-23 long-form article, which is what a public markdown page IS.
 *
 * No `format` tag is required or consulted: NIP-23's contract is that content
 * is markdown, so a 30023 that is not markdown is not a thing the spec allows
 * and is not a case to support. Nothing is fetched and nothing is verified,
 * because there is nothing to fetch -- the relay gave us the body, and the
 * signature over the event already covers it.
 *
 * An empty article is accepted rather than rejected. A published page with no
 * body yet is a real thing an author can make, and showing its title with
 * nothing under it is honest; treating it as missing would not be.
 */
function parseLongForm(event: RawEvent): { format: PageFormat; source: PageSource } {
  return {
    format: 'markdown',
    source: { kind: 'inline', markdown: event.content },
  };
}

/** Our own kind: raw HTML, or a private page. The body is a blob elsewhere. */
function parseBlobBacked(event: RawEvent): { format: PageFormat; source: PageSource } | null {
  // Lowercased before the test rather than after: a hash is a hash whatever
  // case the publisher wrote it in, and rejecting an uppercase one would be a
  // parser being fussy about something that carries no meaning.
  const hash = firstTag(event.tags, 'x')?.toLowerCase();
  if (!hash || !HEX_64.test(hash)) return null;

  const format = firstTag(event.tags, 'format');
  if (format !== 'markdown' && format !== 'html') return null;

  return {
    format,
    source: {
      kind: 'blob',
      hash,
      mimeType: firstTag(event.tags, 'm') ?? defaultMime(format),
      serverHints: allTags(event.tags, 'server').filter(isHttpUrl),
    },
  };
}

function defaultMime(format: PageFormat): string {
  return format === 'markdown' ? 'text/markdown' : 'text/html';
}

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

function parseUnix(value: string | undefined): number | null {
  if (!value) return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/**
 * Layout hints, with an unrecognised value falling back to the default rather
 * than rejecting the page.
 *
 * A future publisher writing `width=full` should get a readable page in a
 * renderer that predates the word, not an error. That is the same
 * graceful-degradation contract the theme event relies on.
 */
function parseLayout(tags: string[][]): PageLayout {
  const width = firstTag(tags, 'width');
  const columns = Number(firstTag(tags, 'columns'));
  return {
    toc: firstTag(tags, 'toc') === 'true',
    width: width === 'narrow' || width === 'wide' ? width : DEFAULT_LAYOUT.width,
    columns: columns === 2 ? 2 : 1,
  };
}
